import apiClient from './api';

const usageService = {
    /**
     * Get user usage statistics
     * @param {number} officeSpaceId - Optional office space ID to get owner's stats
     * @returns {Promise<object>} Usage stats { plan, documentsGenerated, limit, limitResetDate, isLimitReached, imageGenerationCount, imageGenerationLimit, isImageGenerationLimitReached }
     */
    getUserUsageStats: async (officeSpaceId = null) => {
        const params = officeSpaceId ? { officeSpaceId } : {};
        const response = await apiClient.get('/api/usage/stats', { params });
        return response;
    },

    /**
     * Check if user can generate document
     * @returns {Promise<boolean>} True if allowed, false if limit reached
     */
    checkCanGenerate: async () => {
        try {
            const stats = await usageService.getUserUsageStats();
            return !stats.isLimitReached;
        } catch (error) {
            console.error('Failed to check usage limits:', error);
            // Default to allowing if check fails to avoid blocking users on errors
            return true;
    }
    },

    /**
     * Check if user can generate image
     * @returns {Promise<boolean>} True if allowed, false if limit reached
     */
    checkCanGenerateImage: async () => {
        try {
            const stats = await usageService.getUserUsageStats();
            return !stats.isImageGenerationLimitReached;
        } catch (error) {
            console.error('Failed to check image generation limits:', error);
            // Default to allowing if check fails to avoid blocking users on errors
            return true;
        }
    },

    getCreditCosts: async () => {
        const response = await apiClient.get('/api/credits/costs');
        return response.data;
    },

    getCreditConfig: async () => {
        const response = await apiClient.get('/api/credits/config');
        return response.data;
    },

    getCreditStats: async () => {
        const response = await apiClient.get('/api/credits/stats');
        return response.data;
    },

    getCreditHistory: async ({ page = 1, limit = 20, module = null, days = null } = {}) => {
        const params = { page, limit };
        if (module) params.module = module;
        if (days) params.days = days;
        const response = await apiClient.get('/api/credits/history', { params });
        return response.data;
    },

    // ── Credit Wallet migration ──────────────────────────────────────────────
    // Preview what converting legacy credit → wallet would produce (no changes made).
    // → { migrated, rate, legacyCredit, walletCredits, walletBalance }
    getMigrationPreview: async () => {
        const response = await apiClient.get('/api/credits/migrate/preview');
        return response.data;
    },

    // Convert the user's legacy credit into their Credit Wallet. Idempotent.
    migrateCredits: async () => {
        const response = await apiClient.post('/api/credits/migrate');
        return response.data;
    },
};

export default usageService;
